import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  inject,
  signal,
  Signal,
  SimpleChanges,
  effect
} from '@angular/core';
import { utc } from 'moment';
import { TableModule } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';
import { IOHAppService } from 'src/app/services/ioh-app.service';
import { environment } from 'src/environments/environment';
import { positionTerminalPopup } from 'src/app/shared/utils/terminalPopupPosition';

@Component({
  selector: 'ioh-csx-terminal-gate',
  templateUrl: './ioh-csx-terminal-gate.component.html',
  styleUrls: ['./ioh-csx-terminal-gate.component.scss'],
  imports: [CommonModule, TableModule]
})
export class IohTerminalGateComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _terminalName = signal<string>('');
  private _terminalAbbreviation = signal<string>('');
  private _top = signal<number>(0);
  private _left = signal<number>(50);
  private _close = signal<boolean>(false);
  private _showClose = signal<boolean>(false);
  private _collapseAll = signal<boolean>(false);
  //private _level = signal<number>(0);
  private _showFilter = signal<boolean>(false);
  private _gateResponse = signal<any>(null);
  zIndex = 1;
  utc = utc;
  private _terminalUid = signal<string>(null);
  private _dynamicFontSize = signal<string>('');
  private destroy$ = new Subject<void>();
  appService = inject(IOHAppService);
  equipmentInfoTableColumns = [];
  @Input() level: number = 0;
  
  @Input() clickPoint: { x: number; y: number } = null;
  @Input() bounds: { left: number; right: number; top: number; bottom: number } = null;

  rawEquipmentInfoResponse: any;
  displayEquipmentInfoTableData: any;
  totalCounts: {
    totalActiveCount: number;
    totalIdleCount: number;
    totalOfflineCount: number;
  };
  lastUpdatedEQuipmentTableData = [];

  gateInfo = [{}];
  env = environment;

  constructor() {
    // Effect for font size
    effect(() => {
      this._dynamicFontSize.set(this.appService.iohFontSizeValue$());
    });
  }

  @Input()
  set terminalName(value: string) {
    this._terminalName.set(value);
  }
  get terminalName(): string {
    return this._terminalName();
  }

  @Input()
  set gateResponse(value: any) {
    this._gateResponse.set(value);
  }
  get gateResponse(): any {
    return this._gateResponse();
  }

  @Input()
  set terminalAbbreviation(value: string) {
    this._terminalAbbreviation.set(value);
  }
  get terminalAbbreviation(): string {
    return this._terminalAbbreviation();
  }

  @Input()
  set terminalUid(value: any) {
    this._terminalUid.set(value);
  }
  get terminalUid(): any {
    return this._terminalUid();
  }

  @Input()
  set top(value: number) {
    this._top.set(value);
  }
  get top(): number {
    return this._top();
  }

  @Input()
  set left(value: number) {
    this._left.set(value);
  }
  get left(): number {
    return this._left();
  }

  @Input()
  set close(value: boolean) {
    this._close.set(value);
  }
  get close(): boolean {
    return this._close();
  }

  @Input()
  set showClose(value: boolean) {
    this._showClose.set(value);
  }
  get showClose(): boolean {
    return this._showClose();
  }

  @Input()
  set collapseAll(value: boolean) {
    this._collapseAll.set(value);
  }
  get collapseAll(): boolean {
    return this._collapseAll();
  }

  // @Input()
  // set level(value: number) {
  //   this._level.set(value);
  // }
  // get level(): number {
  //   return this._level();
  // }

  @Input()
  set showFilter(value: boolean) {
    this._showFilter.set(value);
  }
  get showFilter(): boolean {
    return this._showFilter();
  }

  @Input()
  set dynamicFontSize(value: string) {
    this._dynamicFontSize.set(value);
  }
  get dynamicFontSize(): string {
    return this._dynamicFontSize();
  }

  @Output() closeChange = new EventEmitter<boolean>();
  @Output() moreEvent = new EventEmitter<any | undefined>();

  ngOnInit() {
    if (this.showClose) {
      this.appService.iohTableZIndexLevels[this.level] += 1;
    }
    this.zIndex = this.appService.iohTableZIndexLevels[this.level];

    this.getEquipmentTableInfo(this._terminalName());
  }

  ngAfterViewInit(): void {
    const element = document.getElementById(this.terminalUid);
    if (element) {
      this.resetElementPosition(element);
      setTimeout(() => this.resetElementPosition(element));
    }
  }

  getEquipmentTableInfo(terminalname) {
    let rawTableResponse = this.gateResponse;
  }

  closeClicked(): void {
    this.moreEvent.emit(0);
    this.closeChange.emit(!this.close);
  }

  resetElementPosition(element: HTMLElement): void {
    positionTerminalPopup(element, this.terminalUid, this.showFilter, this.clickPoint, this.bounds);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes && changes.gateResponse &&
      changes.gateResponse.currentValue !== changes.gateResponse.previousValue
    ) {
      this.getEquipmentTableInfo(this._terminalName());
      //data can arrive after the popup already opened/measured itself - reposition once it has rendered
      const element = document.getElementById(this.terminalUid);
      if (element) {
        setTimeout(() => this.resetElementPosition(element));
      }
    }
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Reset all signals to their default state
    this._terminalName.set('');
    this._terminalAbbreviation.set('');
    this._top.set(0);
    this._left.set(50);
    this._close.set(false);
    this._showClose.set(false);
    this._collapseAll.set(false);
    //this._level.set(0);
    this.level = 0;
    this._showFilter.set(false);
    this._terminalUid.set(null);
    this._dynamicFontSize.set('');

    // Reset component properties
    this.zIndex = 1;

    // Reset app service states if needed
    if (this.showClose) {
      this.appService.iohTableZIndexLevels[this.level] = Math.max(
        0,
        (this.appService.iohTableZIndexLevels[this.level] || 0) - 1
      );
    }
  }
}
